/**
 * `hl7:*` instruments that map an ADT (PID/PV1/PV2) field rather than an OBX
 * observation. Spread into `HL7_INSTRUMENT_REGISTRY`; nothing here is keyed by
 * an observation code.
 */

import type { Hl7Instrument } from './Hl7Instruments.ts';
import { PATIENT_CLASS_ANSWERS } from './Hl7PatientClassAnswers.ts';

const HOSPITAL_SERVICE_INPUT_HINT =
  'The value the data pipeline derives from PV1-10 — the extract emits the ' +
  'field whole and unnormalized (components joined by `^`, repetitions by ' +
  '`~`). A feed sending a bare code matches an exact rule ("CAR"); a feed ' +
  'sending code plus display text needs a wildcard ("CAR^*").';

export const HL7_ADT_INSTRUMENTS: readonly Hl7Instrument[] = [
  {
    // PV1-36 → Encounter.hospitalization.dischargeDisposition. Not a
    // clinical instrument, but consumed via the same `value-mappings`
    // table-type and `hl7:<name>` convention as the assessment scales,
    // so it lives here for picker uniformity. Each row's input is the
    // raw PV1-36 customer code; output is an object
    // `{ code, display, system? }` mapped to a LOINC LL637-0 coding.
    // Runtime constant: `apps/hl7-processor/src/mapping/dischargeDisposition.ts`.
    name: 'discharge-disposition',
    label: 'Discharge Disposition',
    components: null,
    guidance: {
      resultType: 'object',
      description:
        'Maps the raw PV1-36 discharge disposition code to a LOINC discharge ' +
        'status answer (list LL637-0). When `system` is omitted the processor ' +
        'defaults it to LL637-0; only set it for a non-LOINC overlay.',
      inputHint:
        'Raw PV1-36 value exactly as your feed sends it (often a UB-04/NUBC ' +
        'numeric code like "01", "03", "20"). Typical inputs below are UB-04 ' +
        'codes — adjust to your feed.',
      // LL637-0 ("MDSv3_A2100 Discharge status") members, verified against
      // loinc.org and the org terminology server. `system` omitted: the
      // processor's resolveDischargeDisposition applies the LL637-0 default.
      answers: [
        {
          output: {
            code: 'LA10924-1',
            display: 'Community (private home/apt., board/care, assisted living, group home)'
          },
          label: 'Community / home',
          defaultInput: '01'
        },
        {
          output: { code: 'LA9928-8', display: 'Acute hospital' },
          label: 'Acute hospital',
          defaultInput: '02'
        },
        {
          output: { code: 'LA10925-8', display: 'Another nursing home or swing bed' },
          label: 'Nursing home / swing bed',
          defaultInput: '03'
        },
        {
          output: { code: 'LA10064-6', display: 'Psychiatric hospital' },
          label: 'Psychiatric hospital',
          defaultInput: '65'
        },
        {
          output: { code: 'LA10928-2', display: 'Inpatient rehabilitation facility' },
          label: 'Inpatient rehab facility',
          defaultInput: '62'
        },
        {
          output: { code: 'LA10929-0', display: 'ID/DD facility' },
          label: 'ID/DD facility'
        },
        {
          output: { code: 'LA6216-1', display: 'Hospice' },
          label: 'Hospice',
          defaultInput: '50'
        },
        {
          output: { code: 'LA10000-0', display: 'Long-Term Care Hospital (LTCH)' },
          label: 'Long-Term Care Hospital',
          defaultInput: '63'
        },
        {
          output: { code: 'LA10939-9', display: 'Deceased' },
          label: 'Deceased',
          defaultInput: '20'
        },
        { output: { code: 'LA46-8', display: 'Other' }, label: 'Other' }
      ],
      objectTemplate: { code: '', display: '', system: '' }
    }
  },
  {
    // Maps the raw HL7 location/accommodation value (the org's
    // `location-type:custom` hl7-config source, e.g. PV2-40 level-of-care or
    // PV2-2 accommodation code) to an AgileMD location classification, emitted
    // as Location.type and consumed by eCART. Replaces the per-customer Mirth
    // `customLocationType` script. Outputs mirror UNIT_CLASSIFICATIONS in
    // @agilemd/ehr-app-config-schemas (ward/ed/icu/stepdown).
    // Runtime constant: apps/hl7-processor/src/mapping/customLocationType.ts.
    name: 'custom-location-type',
    label: 'Custom Location Type',
    components: null,
    guidance: {
      resultType: 'string',
      description:
        'Maps the raw HL7 location/accommodation value to an AgileMD location ' +
        'classification (ward / ed / icu / stepdown). Unmapped values emit no ' +
        'Location.type.',
      inputHint:
        'Raw value exactly as your feed sends it (e.g. "FLOOR", "ICU", ' +
        '"STEP DOWN"). Add one rule per string your feed produces.',
      answers: [
        { output: 'ward', label: 'Ward', defaultInput: 'FLOOR' },
        { output: 'ed', label: 'Emergency Department (ED)', defaultInput: 'ED' },
        { output: 'icu', label: 'ICU', defaultInput: 'ICU' },
        { output: 'stepdown', label: 'Step-down', defaultInput: 'STEP DOWN' }
      ]
    }
  },
  {
    name: 'hospital-service',
    label: 'Hospital Service',
    components: [
      {
        key: 'service-line',
        label: 'Service Line',
        guidance: {
          resultType: 'object',
          description:
            'Maps the hospital service the data pipeline derives from PV1-10 ' +
            'to an AgileMD service line (`id` + `name`). Consumed by the data ' +
            'pipeline against the `encounter-hospital-service` extract, not by ' +
            'the HL7 processor — publishing this entry changes nothing in the ' +
            'message stream.',
          inputHint: HOSPITAL_SERVICE_INPUT_HINT,
          // `id` is authored as a string — the console's object editor has no
          // numeric value input; the pipeline casts on read.
          objectTemplate: { id: '', name: '' }
        }
      },
      {
        key: 'team',
        label: 'Service Line Team',
        guidance: {
          resultType: 'object',
          description:
            'Maps the portion of PV1-10 the data pipeline splits out for the ' +
            'care team to an AgileMD service line team (`id` + `name`). Same ' +
            'source field as Service Line, evaluated independently.',
          inputHint: HOSPITAL_SERVICE_INPUT_HINT,
          objectTemplate: { id: '', name: '' }
        }
      }
    ]
  },
  {
    // PV1-2 → Encounter.class (v3 ActCode). Runtime:
    // apps/hl7-processor/src/mapping/patientClass.ts.
    name: 'patient-class',
    label: 'Patient Class',
    components: null,
    guidance: {
      resultType: 'string',
      description:
        'Maps the PV1-2 patient class to the v3 ActCode on Encounter.class. Once ' +
        'this entry exists it replaces the built-in patient-class table for the ' +
        'org: an unmatched or dropped value emits no class code.',
      inputHint:
        'PV1-2 code exactly as your feed sends it — usually an HL7 table 0004 ' +
        'letter ("I", "O", "E"), sometimes a word ("INPATIENT"). Add one rule per ' +
        'value your feed produces. Matching is case-sensitive; the built-in ' +
        'table used before this entry existed was not.',
      answers: [...PATIENT_CLASS_ANSWERS]
    }
  }
];
